import { ButtonVariant, CoButton } from "components/CoButton";
import Header from "components/Header";
import ContentWrapper from "components/Layout/ContentWrapper";
import path from "constants/path";
import AskPaperDrawer from "features/AskPaperDrawer";
import { BookmarkLists } from "features/BookmarkLists";
import { CreateNewBookmarkListModal } from "features/Bookmarks";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";
import { useResearchPaperUpload } from "./hooks/useResearchPaperUpload";
import { ResearchHubNavigation, ResearchHubTab } from "./ResearchHubNavigation";
import ResearchHubUploadedPaperResults from "./UploadedPaperResults";

interface ResearchHubProps {
  defaultTab?: ResearchHubTab;
}

export const ResearchHub: React.FC<ResearchHubProps> = ({
  defaultTab = ResearchHubTab.Lists,
}) => {
  const router = useRouter();
  const [labels] = useLabels("ask-paper-upload-new-paper-guide");
  const [selectedTab, setSelectedTab] = useState<ResearchHubTab>(defaultTab);
  const [
    isCreateNewBookmarkListModalOpen,
    setIsCreateNewBookmarkListModalOpen,
  ] = useState(false);

  const { triggerFileSelection, isUploading } = useResearchPaperUpload({
    onUploadTriggered: () => {
      router.push(path.RESEARCH_HUB_UPLOADED);
      setSelectedTab(ResearchHubTab.Uploaded);
    },
    onSuccess: () => {
      toast.success(labels["toast-success"]);
    },
    onError: (errorMessage?: string) => {
      toast.error(errorMessage || "Failed to upload paper. Please try again.");
    },
    actionSource: MixpanelActionSource.ResearchHub,
  });

  const handleClickCreateNewList = () => {
    setIsCreateNewBookmarkListModalOpen(true);
  };

  const handleClickCancelCreateNewList = () => {
    setIsCreateNewBookmarkListModalOpen(false);
  };

  const handleCloseCreateNewListModal = (created?: boolean) => {
    setIsCreateNewBookmarkListModalOpen(false);
    if (created) {
      setSelectedTab(ResearchHubTab.Lists);
      router.push(`/hub/${ResearchHubTab.Lists}`);
    }
  };

  const handleTabClick = (tab: ResearchHubTab) => {
    setSelectedTab(tab);
    router.push(`/hub/${tab}`);
  };

  return (
    <>
      <Header
        title="Research Hub"
        className="border-none"
        actions={
          <>
            <CoButton
              variant={ButtonVariant.Tertiary}
              label="New List"
              icon="icon-plus"
              size="sm"
              onClick={handleClickCreateNewList}
              hideLabelResponsively
            />
            <CoButton
              variant={ButtonVariant.Secondary}
              label={isUploading ? labels["uploading"] : labels["upload-paper"]}
              icon="icon-upload"
              size="sm"
              onClick={triggerFileSelection}
              disabled={isUploading}
              hideLabelResponsively
            />
          </>
        }
      />
      <ResearchHubNavigation
        selectedTab={selectedTab}
        onTabClick={handleTabClick}
      />
      <ContentWrapper className="relative z-0">
        {selectedTab === "lists" ? (
          <BookmarkLists hideHeader />
        ) : (
          <ResearchHubUploadedPaperResults />
        )}
      </ContentWrapper>

      <CreateNewBookmarkListModal
        open={isCreateNewBookmarkListModalOpen}
        onClose={handleCloseCreateNewListModal}
      />

      <AskPaperDrawer />
    </>
  );
};
